import { useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { Household } from '@pacer/shared';
import * as api from '../../api';
import { Icon } from '../../components/Icon';
import { Button, Card, Field, IconButton, Select, cx } from '../../components/ui';
import { queryKeys } from '../../state/queries';

const IDLE_OPTIONS = [1, 2, 3, 5, 10, 15, 30];
const INTERVAL_OPTIONS = [10, 15, 20, 30, 60, 120];

/** Reads a File into the base64 data URL the photos endpoint accepts. */
function toDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result));
    r.onerror = () => reject(new Error(`Could not read ${file.name}`));
    r.readAsDataURL(file);
  });
}

export function ScreensaverPanel({ household }: { household: Household | undefined }) {
  const qc = useQueryClient();
  const fileInput = useRef<HTMLInputElement>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const { data: photos = [] } = useQuery({ queryKey: queryKeys.photos, queryFn: api.getPhotos });

  const save = useMutation({
    mutationFn: (patch: Partial<Household>) => api.updateHousehold(patch),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.household }),
  });

  const upload = async (files: FileList | null): Promise<void> => {
    if (!files?.length) return;
    setBusy(true);
    setError(null);
    try {
      // Sequential: a phone dropping 30 photos at once would otherwise open 30 sockets and
      // push 200MB of base64 through the body parser simultaneously.
      for (const file of Array.from(files)) {
        await api.uploadPhoto(await toDataUrl(file));
      }
      await qc.invalidateQueries({ queryKey: queryKeys.photos });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'upload failed');
    } finally {
      setBusy(false);
      if (fileInput.current) fileInput.current.value = '';
    }
  };

  const remove = useMutation({
    mutationFn: (id: string) => api.deletePhoto(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.photos }),
  });

  const enabled = household?.screensaverEnabled ?? true;

  return (
    <section className="flex flex-col gap-3">
      <h2 className="font-display text-[20px] text-primary">Screensaver</h2>
      <p className="text-[13px] leading-relaxed text-secondary">
        When the wall sits untouched it fades to your photos, with the clock, the weather and
        what's coming up. Any touch brings it back. Phones never do this.
      </p>

      <Card className="flex flex-col gap-4 p-4">
        <label className="flex touch items-center gap-3">
          <input
            type="checkbox"
            checked={enabled}
            onChange={(e) => save.mutate({ screensaverEnabled: e.target.checked })}
            className="size-5 accent-[var(--color-accent)]"
          />
          <span className="text-[14px] text-secondary">Turn on the screensaver</span>
        </label>

        <div className={cx('grid gap-3 sm:grid-cols-2', !enabled && 'pointer-events-none opacity-40')}>
          <Field label="Starts after">
            <Select
              value={String(household?.screensaverIdleMinutes ?? 3)}
              onChange={(e) => save.mutate({ screensaverIdleMinutes: Number(e.target.value) })}
            >
              {IDLE_OPTIONS.map((m) => (
                <option key={m} value={m}>
                  {m} minute{m === 1 ? '' : 's'} idle
                </option>
              ))}
            </Select>
          </Field>

          <Field label="Change photo every">
            <Select
              value={String(household?.screensaverIntervalSeconds ?? 20)}
              onChange={(e) => save.mutate({ screensaverIntervalSeconds: Number(e.target.value) })}
            >
              {INTERVAL_OPTIONS.map((s) => (
                <option key={s} value={s}>
                  {s < 60 ? `${s} seconds` : `${s / 60} minute${s === 60 ? '' : 's'}`}
                </option>
              ))}
            </Select>
          </Field>
        </div>
      </Card>

      <div className="flex items-center gap-3">
        <h3 className="eyebrow">Photos · {photos.length}</h3>
        <Button
          variant="secondary"
          size="sm"
          icon="upload"
          className="ml-auto"
          onClick={() => fileInput.current?.click()}
          disabled={busy}
        >
          {busy ? 'Uploading…' : 'Add photos'}
        </Button>
        <input
          ref={fileInput}
          type="file"
          accept="image/jpeg,image/png,image/webp,image/gif"
          multiple
          className="hidden"
          onChange={(e) => void upload(e.target.files)}
        />
      </div>

      {error && <p className="font-mono text-[12px] text-danger">{error}</p>}

      {photos.length === 0 ? (
        <button
          onClick={() => fileInput.current?.click()}
          className="flex flex-col items-center justify-center gap-2 rounded-lg border border-dashed border-border-subtle bg-surface/30 px-6 py-12 transition-colors hover:border-border hover:bg-surface-high/40"
        >
          <Icon name="image" size={24} className="text-muted" />
          <span className="text-[14px] text-secondary">Add photos</span>
          <span className="font-mono text-[12px] text-muted">
            JPEG, PNG, WebP or GIF · up to 8MB each
          </span>
        </button>
      ) : (
        <div className="grid grid-cols-3 gap-2 sm:grid-cols-4">
          {photos.map((p) => (
            <div
              key={p.id}
              className="group relative aspect-square overflow-hidden rounded-md border border-border-subtle"
            >
              <img src={p.url} alt="" className="size-full object-cover" loading="lazy" />
              <IconButton
                icon="trash"
                label="Delete photo"
                size="sm"
                onClick={() => remove.mutate(p.id)}
                className="absolute top-1 right-1 bg-black/60 text-white opacity-0 backdrop-blur-sm transition-opacity group-hover:opacity-100 focus:opacity-100"
              />
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
